import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CreateStreamModal } from "./CreateStreamModal";
import { useAuth } from "@/hooks/useAuth";
import type { Organization, OrganizationRole } from "@shared/schema";
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  LogOut,
  Plus,
  Terminal,
  CalendarDays,
  BarChart3,
  Link as LinkIcon,
  Menu,
  X,
} from "lucide-react";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  
  // Fetch user's organizations
  const { data: organizations } = useQuery<Array<Organization & { role: OrganizationRole }>>({
    queryKey: ["/api/organizations"],
  });
  
  const currentOrg = organizations?.[0];
  const userName = user?.name || user?.email?.split("@")[0] || "User";
  const userInitials = userName
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  const handleLogout = () => {
    logout();
  };

  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/calendar", label: "Calendar", icon: CalendarDays },
    { path: "/platforms", label: "Platforms", icon: LinkIcon },
    { path: "/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/templates", label: "Templates", icon: FileText },
    { path: "/speakers", label: "Speakers", icon: Users },
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-sidebar border-r border-sidebar-border flex-shrink-0 flex flex-col
        transform transition-transform duration-200 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-testid="sidebar">
        <div className="p-4 lg:p-6 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Terminal className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-sidebar-foreground">Stream Manager</h1>
                <p className="text-xs text-sidebar-foreground/70 truncate" data-testid="text-org-name">
                  {currentOrg?.name || "Loading..."}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-sidebar-foreground h-11 w-11 flex-shrink-0"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="button-close-mobile-menu"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.path;
              
              // Add data-tour attributes for specific nav items
              const tourAttr = item.label === "Speakers" ? "speakers-nav" 
                : item.label === "Platforms" ? "platforms-nav"
                : item.label === "Settings" ? "settings-nav"
                : undefined;
              
              return (
                <Link 
                  key={item.path} 
                  href={item.path}
                  className={`nav-item flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                    isActive 
                      ? "active bg-sidebar-accent text-sidebar-accent-foreground" 
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  }`}
                  data-testid={`link-nav-${item.label.toLowerCase()}`}
                  data-tour={tourAttr}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 w-64 p-4 lg:p-6 border-t border-sidebar-border bg-sidebar">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center font-semibold text-sm">
              {userInitials}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-sidebar-foreground" data-testid="text-user-name">
                {userName}
              </p>
              <p className="text-xs text-sidebar-foreground/70 capitalize" data-testid="text-user-role">
                {currentOrg?.role || "Member"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-sidebar-foreground/70 hover:text-sidebar-foreground h-11 w-11"
              onClick={handleLogout}
              data-testid="button-logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto lg:ml-0">
        {/* Top Bar */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="px-4 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-4">
            {/* Mobile Hamburger Menu */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-card-foreground h-11 w-11"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="button-open-mobile-menu"
            >
              <Menu className="w-6 h-6" />
            </Button>

            <div className="flex-1 min-w-0">
              <h2 className="text-xl lg:text-2xl font-bold text-card-foreground truncate">
                {location === "/" && "Live Stream Dashboard"}
                {location === "/calendar" && "Stream Calendar"}
                {location === "/platforms" && "Platform Connections"}
                {location === "/analytics" && "Stream Analytics"}
                {location === "/templates" && "Description Templates"}
                {location === "/speakers" && "Speaker Management"}
                {location === "/settings" && "Settings"}
              </h2>
              <p className="text-xs lg:text-sm text-muted-foreground hidden sm:block truncate">
                {location === "/" && "Manage all your streaming platforms in one place"}
                {location === "/calendar" && "View and manage your scheduled live streams"}
                {location === "/platforms" && "Manage your social media accounts and track streaming activity"}
                {location === "/analytics" && "Track performance and engagement metrics"}
                {location === "/templates" && "Create and manage templates for your stream descriptions"}
                {location === "/speakers" && "Manage your church speakers and pastors"}
                {location === "/settings" && "Configure your streaming settings"}
              </p>
            </div>
            {location === "/" && (
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="btn-primary px-3 lg:px-6 py-2 lg:py-3 rounded-lg font-medium flex items-center gap-2 shadow-sm flex-shrink-0"
                data-testid="button-create-stream"
                data-tour="create-stream-button"
              >
                <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
                <span className="hidden sm:inline">Create New Stream</span>
                <span className="sm:hidden">New</span>
              </Button>
            )}
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>

      <CreateStreamModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}
